import { useTranslation, Trans } from "react-i18next";
import styles from "../SsnCardVerification.module.scss";
import { usePageView } from "../../../shared/analytics/usePageView";
import { trackClicked } from "../../../shared/analytics/events";
import { DOCUMENT_CAPTURE_EVENTS, DOCUMENT_CAPTURE_SECTIONS } from "../../../shared/analytics/documentCaptureEvents";
import { useShowPartnerFooter } from "../../brandLogo/BrandLogo";

interface InfoScreenProps {
  onOpenCamera: () => void;
  onNoId: () => void;
  consumerLogo?: { src: string; alt: string };
}

/**
 * InfoScreen — the opening instructions screen for the SSN card flow (state `ready`).
 *
 * Mirrors legal-id's IntroScreen: heading, document illustration, an info list,
 * optional consumer-logo header/footer, and the "Open camera" button that
 * advances to capture. Unsupervised flow — capture handles front + back in a
 * single Regula mount (Option 3), so the copy mentions both sides.
 *
 * TODO: swap `legal-id-image.svg` for an SSN-card-specific illustration when one
 * is available (reused here as a placeholder to avoid a broken asset).
 */
export function InfoScreen({ onOpenCamera, onNoId, consumerLogo }: InfoScreenProps) {
  const { t } = useTranslation();
  const showPartnerFooter = useShowPartnerFooter(consumerLogo);
  usePageView(DOCUMENT_CAPTURE_EVENTS.VIEWED.INFO_SCREEN, "section", { section: DOCUMENT_CAPTURE_SECTIONS.INFO });

  const handleOpenCamera = () => {
    trackClicked(DOCUMENT_CAPTURE_EVENTS.CLICKED.START_DOCUMENT_CAPTURE, "button", { section: DOCUMENT_CAPTURE_SECTIONS.INFO, targetAction: "open" });
    onOpenCamera();
  };

  const handleNoId = () => {
    trackClicked(DOCUMENT_CAPTURE_EVENTS.CLICKED.REPORT_NO_DOCUMENT, "link", { section: DOCUMENT_CAPTURE_SECTIONS.INFO, targetAction: "navigate" });
    onNoId();
  };

  return (
    <div className={styles.card}>
      {consumerLogo && (
        <div className={styles.cardConsumerLogoHeader}>
          <img src={consumerLogo.src} alt={consumerLogo.alt} />
          <div className={styles.cardConsumerLogoDivider} />
        </div>
      )}
      <h2 className={styles.title}>{t('ssnCard.ready.heading')}</h2>

      <p className={styles.subtitle}>{t('ssnCard.ready.subtitle')}</p>

      <div className={styles.infoList}>
          <div className={styles.infoItem}>
              <img src="/assets/svg/count-one.svg" alt="" aria-hidden="true" className={styles.infoIcon} />
              <span className={styles.infoText}>{t('ssnCard.ready.info1')}</span>
          </div>
        <div className={styles.infoItem}>
          <img src="/assets/svg/count-two.svg" alt="" aria-hidden="true" className={styles.infoIcon} />
          <span className={styles.infoText}>{t('ssnCard.ready.info2')}</span>
        </div>
        <div className={styles.infoItem}>
          <img src="/assets/svg/count-three.svg" alt="" aria-hidden="true" className={styles.infoIcon} />
          <span className={styles.infoText}>{t('ssnCard.ready.info3')}</span>
        </div>
          <div className={styles.infoItem}>
              <img src="/assets/svg/count-four.svg" alt="" aria-hidden="true" className={styles.infoIcon} />
              <span className={styles.infoText}>{t('ssnCard.ready.info4')}</span>
          </div>
      </div>

      <button type="button" className={styles.openCameraButton} onClick={handleOpenCamera}>
        {t('ssnCard.ready.button')}
      </button>

      <button type="button" className={styles.noIdLink} onClick={handleNoId}>
        {t('ssnCard.ready.noId')}
      </button>

      {showPartnerFooter && (
        <p className={styles.partnerFooter}>
          <Trans
            i18nKey="ssnCard.partnerFooter"
            components={[
              <a
                key="idme-link"
                href="https://id.me"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.partnerFooterLink}
              />
            ]}
          />
        </p>
      )}
    </div>
  );
}
